/**
 * Unit staleness policy.
 *
 * Mirrors the Android `UnitStalenessPolicy` rules:
 *  - A unit that has not reported a position for {@link FADE_START_MS}
 *    starts fading.
 *  - Between {@link FADE_START_MS} and {@link FADE_END_MS} the marker
 *    opacity decays linearly from 1.0 to 0.0.
 *  - At or beyond {@link FADE_END_MS} the unit is considered stale and
 *    should be evicted from the store entirely.
 *
 * Markers (user-placed annotations) are intentionally excluded.
 */

export const FADE_START_MS = 15 * 60_000;
export const FADE_END_MS = 30 * 60_000;

/**
 * Compute the opacity for a unit given the timestamp of its last update.
 *
 * Defensive handling:
 *  - `null` / `undefined` / non-finite / non-positive timestamps are treated
 *    as "fresh" (opacity 1.0) rather than ghosted — we never want a malformed
 *    timestamp to silently hide a unit.
 *  - Future-dated timestamps are also treated as fresh.
 *
 * @param lastUpdateMs Epoch milliseconds of the unit's last position update,
 *                     or `null` / `undefined` if unknown.
 * @param now          Current epoch milliseconds (injected for testability).
 * @returns Opacity in the closed interval [0.0, 1.0].
 */
export function computeOpacity(
    lastUpdateMs: number | null | undefined,
    now: number,
): number {
    if (
        lastUpdateMs === null ||
        lastUpdateMs === undefined ||
        !Number.isFinite(lastUpdateMs) ||
        lastUpdateMs <= 0
    ) {
        return 1.0;
    }

    const ageMs = now - lastUpdateMs;
    if (ageMs <= FADE_START_MS) {
        return 1.0;
    }
    if (ageMs >= FADE_END_MS) {
        return 0.0;
    }

    const fadeWindow = FADE_END_MS - FADE_START_MS;
    const progress = (ageMs - FADE_START_MS) / fadeWindow;
    return 1.0 - progress;
}

export interface StalenessCandidate {
    id: string;
    lastUpdateMs: number | null | undefined;
}

/**
 * Return IDs of units whose last update is older than `maxAgeMs`.
 *
 * Units with a missing / malformed / future timestamp are never reported —
 * mirroring the "defensive fresh" rule in {@link computeOpacity}.
 */
export function selectStaleIds(
    units: ReadonlyArray<StalenessCandidate>,
    now: number,
    maxAgeMs: number = FADE_END_MS,
): string[] {
    const stale: string[] = [];
    for (const u of units) {
        const ts = u.lastUpdateMs;
        if (
            ts === null ||
            ts === undefined ||
            !Number.isFinite(ts) ||
            ts <= 0
        ) {
            continue;
        }
        if (now - ts >= maxAgeMs) {
            stale.push(u.id);
        }
    }
    return stale;
}
